import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

// 팀 정보 타입 (참조용, 저장은 하지 않음)
export interface MemberTeamInfo {
  teamId: string;
  teamName: string;
  permissionWriting: boolean;
  permissionReading: boolean;
}

// 멤버 정보 타입
export interface MemberInfo {
  appUserId: number;
  email: string;
  name: string;
  phone: string | null;
  organizationRole: string;
  organizationAppUserStatus: string;
  teamInfoList?: MemberTeamInfo[];
}

export type OrganizationRole = 'ORGANIZATION_OWNER' | 'ORGANIZATION_ADMIN' | 'ORGANIZATION_USER';

// 팀 멤버 응답 타입
export interface TeamMembersResponse {
  type: string;
  id: string;
  teamAppUserCount?: number;
  teamAppUsers?: MemberInfo[];
}

// 조직 멤버 응답 타입
export interface OrganizationMembersResponse {
  organizationId: string;
  organizationAppUserCount: number;
  organizationAppUserInfoList: MemberInfo[];
}

// 멤버 스토어 상태 타입
interface MembersState {
  // === 상태 ===
  // UserId를 키로 멤버 정보를 관리
  membersByUserId: Record<number, MemberInfo>;

  // organizationId-userId를 합성 키로 사용하여 해당 조직에 소속된 멤버 아이디 목록 관리
  organizationMemberIds: Record<string, number[]>;

  // teamId-userId를 합성 키로 사용하여 해당 팀에 소속된 멤버 아이디 목록 관리
  teamMemberIds: Record<string, number[]>;

  // === 액션 ===
  // 멤버 추가 또는 업데이트
  setMember: (member: MemberInfo) => void;

  // 여러 멤버 추가 또는 업데이트
  setMembers: (members: MemberInfo[]) => void;

  // 조직에 멤버 추가
  addMembersToOrganization: (organizationId: string, members: MemberInfo[]) => void;

  // 팀에 멤버 추가
  addMembersToTeam: (teamId: string, members: MemberInfo[]) => void;

  // 특정 멤버 업데이트
  updateMember: (memberId: number, updatedMember: Partial<MemberInfo>) => void;

  // 특정 멤버 삭제
  removeMember: (memberId: number) => void;

  // 특정 조직에서 멤버 삭제
  removeMemberFromOrganization: (organizationId: string, memberId: number) => void;

  // 특정 팀에서 멤버 삭제
  removeMemberFromTeam: (teamId: string, memberId: number) => void;

  // 조직에 응답 데이터 적용
  updateOrganizationMembers: (organizationId: string, response: OrganizationMembersResponse) => void;

  // 팀에 응답 데이터 적용
  updateTeamMembers: (teamId: string, response: TeamMembersResponse) => void;

  // 스토어 초기화
  clearMembers: () => void;

  // === 접근자 ===
  // 조직 내 모든 멤버 가져오기
  getOrganizationMembers: (organizationId: string) => MemberInfo[];

  // 팀 내 모든 멤버 가져오기
  getTeamMembers: (teamId: string) => MemberInfo[];

  // 특정 멤버 가져오기
  getMemberById: (memberId: number) => MemberInfo | undefined;

  // 특정 이메일로 멤버 가져오기
  getMemberByEmail: (email: string) => MemberInfo | undefined;
}

export const useMembersStore = create<MembersState>()(
  devtools(
    persist(
      (set, get) => ({
        // === 초기 상태 ===
        membersByUserId: {},
        organizationMemberIds: {},
        teamMemberIds: {},

        // === 액션 ===
        setMember: (member) => {
          set((state) => ({
            membersByUserId: {
              ...state.membersByUserId,
              [member.appUserId]: member
            }
          }));
        },

        setMembers: (members) => {
          const newMembersByUserId = { ...get().membersByUserId };

          members.forEach((member) => {
            newMembersByUserId[member.appUserId] = member;
          });

          set({ membersByUserId: newMembersByUserId });
        },

        addMembersToOrganization: (organizationId, members) => {
          // 먼저 멤버 데이터 저장
          get().setMembers(members);

          set((state) => {
            // 기존 조직의 멤버 ID 목록 가져오기
            const existingMemberIds = state.organizationMemberIds[organizationId] || [];

            // 새 멤버 ID 목록 (중복 제거)
            const newMemberIds = Array.from(new Set([...existingMemberIds, ...members.map((m) => m.appUserId)]));

            return {
              organizationMemberIds: {
                ...state.organizationMemberIds,
                [organizationId]: newMemberIds
              }
            };
          });
        },

        addMembersToTeam: (teamId, members) => {
          // 먼저 멤버 데이터 저장
          get().setMembers(members);

          set((state) => {
            // 기존 팀의 멤버 ID 목록 가져오기
            const existingMemberIds = state.teamMemberIds[teamId] || [];

            // 새 멤버 ID 목록 (중복 제거)
            const newMemberIds = Array.from(new Set([...existingMemberIds, ...members.map((m) => m.appUserId)]));

            return {
              teamMemberIds: {
                ...state.teamMemberIds,
                [teamId]: newMemberIds
              }
            };
          });
        },

        updateMember: (memberId, updatedMember) => {
          set((state) => {
            // 기존 멤버가 없으면 무시
            if (!state.membersByUserId[memberId]) return state;

            return {
              membersByUserId: {
                ...state.membersByUserId,
                [memberId]: {
                  ...state.membersByUserId[memberId],
                  ...updatedMember
                }
              }
            };
          });
        },

        removeMember: (memberId) => {
          set((state) => {
            // 멤버 ID로 삭제
            const { [memberId]: removedMember, ...restMembers } = state.membersByUserId;

            // 모든 조직 및 팀 목록에서 해당 멤버 ID 제거
            const newOrganizationMemberIds = { ...state.organizationMemberIds };
            Object.keys(newOrganizationMemberIds).forEach((orgId) => {
              newOrganizationMemberIds[orgId] = newOrganizationMemberIds[orgId].filter((id) => id !== memberId);
            });

            const newTeamMemberIds = { ...state.teamMemberIds };
            Object.keys(newTeamMemberIds).forEach((teamId) => {
              newTeamMemberIds[teamId] = newTeamMemberIds[teamId].filter((id) => id !== memberId);
            });

            return {
              membersByUserId: restMembers,
              organizationMemberIds: newOrganizationMemberIds,
              teamMemberIds: newTeamMemberIds
            };
          });
        },

        removeMemberFromOrganization: (organizationId, memberId) => {
          set((state) => {
            // 조직 멤버 목록에서 삭제
            const orgMemberIds = state.organizationMemberIds[organizationId] || [];
            const updatedOrgMemberIds = orgMemberIds.filter((id) => id !== memberId);

            return {
              organizationMemberIds: {
                ...state.organizationMemberIds,
                [organizationId]: updatedOrgMemberIds
              }
            };
          });
        },

        removeMemberFromTeam: (teamId, memberId) => {
          set((state) => {
            // 팀 멤버 목록에서 삭제
            const teamMemberIds = state.teamMemberIds[teamId] || [];
            const updatedTeamMemberIds = teamMemberIds.filter((id) => id !== memberId);

            return {
              teamMemberIds: {
                ...state.teamMemberIds,
                [teamId]: updatedTeamMemberIds
              }
            };
          });
        },

        updateOrganizationMembers: (organizationId, response) => {
          const { organizationAppUserInfoList } = response;

          // 멤버 데이터 저장
          get().setMembers(organizationAppUserInfoList);

          // 조직-멤버 관계 저장
          set((state) => ({
            organizationMemberIds: {
              ...state.organizationMemberIds,
              [organizationId]: organizationAppUserInfoList.map((m) => m.appUserId)
            }
          }));
        },

        updateTeamMembers: (teamId, response) => {
          const { teamAppUsers } = response;

          // 멤버 데이터 저장
          get().setMembers(teamAppUsers || []);

          // 팀-멤버 관계 저장
          set((state) => ({
            teamMemberIds: {
              ...state.teamMemberIds,
              [teamId]: teamAppUsers?.map((m) => m.appUserId) || []
            }
          }));
        },

        clearMembers: () => {
          set({
            membersByUserId: {},
            organizationMemberIds: {},
            teamMemberIds: {}
          });
        },

        // === 접근자 ===
        getOrganizationMembers: (organizationId) => {
          const state = get();
          const memberIds = state.organizationMemberIds[organizationId] || [];

          return memberIds.map((id) => state.membersByUserId[id]).filter((member) => member !== undefined) as MemberInfo[];
        },

        getTeamMembers: (teamId) => {
          const state = get();
          const memberIds = state.teamMemberIds[teamId] || [];

          return memberIds.map((id) => state.membersByUserId[id]).filter((member) => member !== undefined) as MemberInfo[];
        },

        getMemberById: (memberId) => {
          return get().membersByUserId[memberId];
        },

        getMemberByEmail: (email) => {
          const allMembers = Object.values(get().membersByUserId);
          return allMembers.find((member) => member.email === email);
        }
      }),
      {
        name: 'members-storage',
        partialize: (state) => ({
          membersByUserId: state.membersByUserId,
          organizationMemberIds: state.organizationMemberIds,
          teamMemberIds: state.teamMemberIds
        }),
        storage: createJSONStorage(() => sessionStorage)
      }
    ),
    { name: 'MembersStore' }
  )
);
